import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { authFetch } from '../../utils/authFetch';

const EstateDetail = () => {
    const { id } = useParams();
    const [estate, setEstate] = useState(null);
    const [assets, setAssets] = useState([]);
    const [liabilities, setLiabilities] = useState([]);
    const [beneficiaries, setBeneficiaries] = useState([]);
    const [timeline, setTimeline] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [activeTab, setActiveTab] = useState('overview');
    const [loading, setLoading] = useState(true);
    const [showAddAsset, setShowAddAsset] = useState(false);
    const [showAddLiability, setShowAddLiability] = useState(false);
    const [showAddBeneficiary, setShowAddBeneficiary] = useState(false);
    const [showAddMilestone, setShowAddMilestone] = useState(false);

    useEffect(() => { fetchEstate(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchEstate = async () => {
        setLoading(true);
        try {
            const [estateRes, assetsRes, liabilitiesRes, beneficiariesRes, timelineRes, docsRes] = await Promise.all([
                authFetch(`/api/estates/estates/${id}/`),
                authFetch(`/api/estates/estates/${id}/assets/`),
                authFetch(`/api/estates/estates/${id}/liabilities/`),
                authFetch(`/api/estates/estates/${id}/beneficiaries/`),
                authFetch(`/api/estates/estates/${id}/timeline/`),
                authFetch(`/api/estates/estates/${id}/documents/`),
            ]);
            if (estateRes.ok) setEstate(await estateRes.json());
            if (assetsRes.ok) setAssets(await assetsRes.json());
            if (liabilitiesRes.ok) setLiabilities(await liabilitiesRes.json());
            if (beneficiariesRes.ok) setBeneficiaries(await beneficiariesRes.json());
            if (timelineRes.ok) setTimeline(await timelineRes.json());
            if (docsRes.ok) setDocuments(await docsRes.json());
        } catch (err) {
            console.error('Failed to fetch estate:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleRecalculate = async () => {
        try {
            const res = await authFetch(`/api/estates/estates/${id}/recalculate/`, { method: 'POST' });
            if (res.ok) {
                const data = await res.json();
                setEstate(prev => ({ ...prev, ...data }));
            }
        } catch (err) {
            console.error('Recalculate failed:', err);
        }
    };

    const handleAddAsset = async (e) => {
        e.preventDefault();
        const form = new FormData(e.target);
        const data = { estate: parseInt(id) };
        for (const [key, value] of form.entries()) { data[key] = value; }
        try {
            const res = await authFetch('/api/estates/assets/', { method: 'POST', body: JSON.stringify(data) });
            if (res.ok) {
                setShowAddAsset(false);
                fetchEstate();
            }
        } catch (err) { console.error('Add asset failed:', err); }
    };

    const handleAddLiability = async (e) => {
        e.preventDefault();
        const form = new FormData(e.target);
        const data = { estate: parseInt(id) };
        for (const [key, value] of form.entries()) { data[key] = value; }
        try {
            const res = await authFetch('/api/estates/liabilities/', { method: 'POST', body: JSON.stringify(data) });
            if (res.ok) {
                setShowAddLiability(false);
                fetchEstate();
            }
        } catch (err) { console.error('Add liability failed:', err); }
    };

    const handleAddBeneficiary = async (e) => {
        e.preventDefault();
        const form = new FormData(e.target);
        const data = { estate: parseInt(id) };
        for (const [key, value] of form.entries()) { data[key] = value; }
        if (data.is_minor === 'on') data.is_minor = true;
        else data.is_minor = false;
        try {
            const res = await authFetch('/api/estates/beneficiaries/', { method: 'POST', body: JSON.stringify(data) });
            if (res.ok) {
                setShowAddBeneficiary(false);
                fetchEstate();
            }
        } catch (err) { console.error('Add beneficiary failed:', err); }
    };

    const handleAddMilestone = async (e) => {
        e.preventDefault();
        const form = new FormData(e.target);
        const data = { estate: parseInt(id) };
        for (const [key, value] of form.entries()) { data[key] = value; }
        try {
            const res = await authFetch('/api/estates/timeline/', { method: 'POST', body: JSON.stringify(data) });
            if (res.ok) {
                setShowAddMilestone(false);
                fetchEstate();
            }
        } catch (err) { console.error('Add milestone failed:', err); }
    };

    const handleCompleteMilestone = async (milestoneId) => {
        try {
            const res = await authFetch(`/api/estates/timeline/${milestoneId}/complete/`, { method: 'POST' });
            if (res.ok) fetchEstate();
        } catch (err) { console.error('Complete milestone failed:', err); }
    };

    const handleMarkLiabilityPaid = async (liabilityId) => {
        try {
            const res = await authFetch(`/api/estates/liabilities/${liabilityId}/mark_paid/`, { method: 'POST', body: JSON.stringify({}) });
            if (res.ok) fetchEstate();
        } catch (err) { console.error('Mark paid failed:', err); }
    };

    const formatCurrency = (amount, currency = 'USD') => {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount || 0);
    };

    const statusColors = {
        INTAKE: 'bg-blue-100 text-blue-700',
        PROBATE: 'bg-yellow-100 text-yellow-700',
        ADMINISTRATION: 'bg-purple-100 text-purple-700',
        DISTRIBUTION: 'bg-green-100 text-green-700',
        CLOSED: 'bg-gray-100 text-gray-600',
        DISPUTED: 'bg-red-100 text-red-700',
    };

    if (loading) {
        return (
            <div className="p-6 bg-gray-50 min-h-screen">
                <div className="h-8 w-64 bg-gray-200 rounded animate-pulse mb-4"></div>
                <div className="h-4 w-96 bg-gray-100 rounded animate-pulse mb-8"></div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    {[1, 2, 3].map(i => <div key={i} className="bg-white rounded-xl p-6 h-32 animate-pulse"></div>)}
                </div>
            </div>
        );
    }

    if (!estate) {
        return (
            <div className="p-6 bg-gray-50 min-h-screen">
                <div className="text-center mt-16">
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Estate Not Found</h2>
                    <Link to="/estates/list" className="text-indigo-600 hover:underline">Back to Estates</Link>
                </div>
            </div>
        );
    }

    const tabs = [
        { id: 'overview', label: 'Overview' },
        { id: 'assets', label: `Assets (${assets.length})` },
        { id: 'liabilities', label: `Liabilities (${liabilities.length})` },
        { id: 'beneficiaries', label: `Beneficiaries (${beneficiaries.length})` },
        { id: 'timeline', label: `Timeline (${timeline.length})` },
        { id: 'documents', label: `Documents (${documents.length})` },
    ];

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            {/* Breadcrumb & Header */}
            <div className="mb-6">
                <nav className="text-sm mb-2">
                    <Link to="/estates" className="text-gray-500 hover:text-indigo-600">Estates</Link>
                    <span className="mx-2 text-gray-400">/</span>
                    <Link to="/estates/list" className="text-gray-500 hover:text-indigo-600">All Estates</Link>
                    <span className="mx-2 text-gray-400">/</span>
                    <span className="text-gray-900">{estate.deceased_name}</span>
                </nav>
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Estate of {estate.deceased_name}</h1>
                        <div className="flex items-center gap-3 mt-2">
                            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusColors[estate.status] || 'bg-gray-100 text-gray-600'}`}>
                                {estate.status_display}
                            </span>
                            <span className="text-sm text-gray-500">{estate.estate_reference}</span>
                            <span className="text-sm text-gray-500">• {estate.estate_type_display}</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={handleRecalculate} className="px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                            <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                            Recalculate
                        </button>
                        {estate.trust_account && (
                            <Link to={`/trust/accounts/${estate.trust_account}`} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                                View Trust Account
                            </Link>
                        )}
                    </div>
                </div>
            </div>

            {/* Financial Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <p className="text-sm font-medium text-gray-500 mb-1">Total Assets</p>
                    <p className="text-2xl font-bold text-green-600">{formatCurrency(estate.total_assets_value, estate.currency)}</p>
                    <p className="text-xs text-gray-400 mt-1">{estate.assets_count} asset(s)</p>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <p className="text-sm font-medium text-gray-500 mb-1">Total Liabilities</p>
                    <p className="text-2xl font-bold text-red-600">{formatCurrency(estate.total_liabilities_value, estate.currency)}</p>
                    <p className="text-xs text-gray-400 mt-1">{estate.liabilities_count} liability(ies)</p>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <p className="text-sm font-medium text-gray-500 mb-1">Net Estate Value</p>
                    <p className={`text-2xl font-bold ${estate.net_estate_value >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatCurrency(estate.net_estate_value, estate.currency)}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">{estate.beneficiaries_count} beneficiary(ies)</p>
                </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                <div className="border-b border-gray-200">
                    <nav className="flex space-x-0 overflow-x-auto">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                                    activeTab === tab.id
                                        ? 'border-indigo-600 text-indigo-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>

                <div className="p-6">
                    {/* Overview Tab */}
                    {activeTab === 'overview' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Deceased Information</h3>
                                <dl className="space-y-3">
                                    <div className="flex justify-between"><dt className="text-sm text-gray-500">Full Name</dt><dd className="text-sm font-medium text-gray-900">{estate.deceased_name}</dd></div>
                                    {estate.deceased_id_number && <div className="flex justify-between"><dt className="text-sm text-gray-500">ID Number</dt><dd className="text-sm font-medium text-gray-900">{estate.deceased_id_number}</dd></div>}
                                    <div className="flex justify-between"><dt className="text-sm text-gray-500">Date of Death</dt><dd className="text-sm font-medium text-gray-900">{estate.date_of_death}</dd></div>
                                    {estate.place_of_death && <div className="flex justify-between"><dt className="text-sm text-gray-500">Place of Death</dt><dd className="text-sm font-medium text-gray-900">{estate.place_of_death}</dd></div>}
                                    {estate.last_known_address && <div className="flex justify-between"><dt className="text-sm text-gray-500">Last Address</dt><dd className="text-sm font-medium text-gray-900 text-right max-w-xs">{estate.last_known_address}</dd></div>}
                                </dl>
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Estate Details</h3>
                                <dl className="space-y-3">
                                    <div className="flex justify-between"><dt className="text-sm text-gray-500">Client / Executor</dt><dd className="text-sm font-medium text-gray-900">{estate.client_name}</dd></div>
                                    <div className="flex justify-between"><dt className="text-sm text-gray-500">Assigned To</dt><dd className="text-sm font-medium text-gray-900">{estate.assigned_to_name}</dd></div>
                                    {estate.court_case_number && <div className="flex justify-between"><dt className="text-sm text-gray-500">Court Case #</dt><dd className="text-sm font-medium text-gray-900">{estate.court_case_number}</dd></div>}
                                    {estate.master_reference && <div className="flex justify-between"><dt className="text-sm text-gray-500">Master Reference</dt><dd className="text-sm font-medium text-gray-900">{estate.master_reference}</dd></div>}
                                    {estate.will_date && <div className="flex justify-between"><dt className="text-sm text-gray-500">Will Date</dt><dd className="text-sm font-medium text-gray-900">{estate.will_date}</dd></div>}
                                    {estate.will_location && <div className="flex justify-between"><dt className="text-sm text-gray-500">Will Location</dt><dd className="text-sm font-medium text-gray-900">{estate.will_location}</dd></div>}
                                    {estate.trust_account_name && <div className="flex justify-between"><dt className="text-sm text-gray-500">Trust Account</dt><dd className="text-sm font-medium text-indigo-600"><Link to={`/trust/accounts/${estate.trust_account}`}>{estate.trust_account_name}</Link></dd></div>}
                                </dl>
                            </div>
                            {estate.notes && (
                                <div className="md:col-span-2">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Notes</h3>
                                    <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-4">{estate.notes}</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Assets Tab */}
                    {activeTab === 'assets' && (
                        <div>
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">Estate Assets</h3>
                                <button onClick={() => setShowAddAsset(true)} className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors">
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                                    Add Asset
                                </button>
                            </div>
                            {assets.length === 0 ? (
                                <div className="text-center py-12 text-gray-500">
                                    <p className="mb-2">No assets recorded yet</p>
                                    <button onClick={() => setShowAddAsset(true)} className="text-indigo-600 hover:underline text-sm">Add the first asset</button>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {assets.map(asset => (
                                        <div key={asset.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                            <div className="flex items-center">
                                                <div className={`p-2 rounded-lg ${asset.is_distributed ? 'bg-green-100' : 'bg-blue-100'}`}>
                                                    <svg className={`w-5 h-5 ${asset.is_distributed ? 'text-green-600' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                                    </svg>
                                                </div>
                                                <div className="ml-3">
                                                    <p className="font-medium text-gray-900">{asset.description}</p>
                                                    <p className="text-sm text-gray-500">{asset.asset_type_display}{asset.location ? ` • ${asset.location}` : ''}</p>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-semibold text-gray-900">{formatCurrency(asset.estimated_value, estate.currency)}</p>
                                                {asset.is_distributed && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Distributed</span>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Liabilities Tab */}
                    {activeTab === 'liabilities' && (
                        <div>
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">Estate Liabilities</h3>
                                <button onClick={() => setShowAddLiability(true)} className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors">
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                                    Add Liability
                                </button>
                            </div>
                            {liabilities.length === 0 ? (
                                <div className="text-center py-12 text-gray-500">
                                    <p className="mb-2">No liabilities recorded yet</p>
                                    <button onClick={() => setShowAddLiability(true)} className="text-indigo-600 hover:underline text-sm">Add the first liability</button>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {liabilities.map(liability => (
                                        <div key={liability.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                            <div className="flex items-center">
                                                <div className={`p-2 rounded-lg ${liability.is_paid ? 'bg-green-100' : 'bg-red-100'}`}>
                                                    <svg className={`w-5 h-5 ${liability.is_paid ? 'text-green-600' : 'text-red-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2zM10 8.5a.5.5 0 11-1 0 .5.5 0 011 0zm5 5a.5.5 0 11-1 0 .5.5 0 011 0z" />
                                                    </svg>
                                                </div>
                                                <div className="ml-3">
                                                    <p className="font-medium text-gray-900">{liability.creditor_name}</p>
                                                    <p className="text-sm text-gray-500">{liability.liability_type_display} • {liability.description}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <div className="text-right">
                                                    <p className="font-semibold text-gray-900">{formatCurrency(liability.amount, estate.currency)}</p>
                                                    {liability.is_paid ? (
                                                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Paid</span>
                                                    ) : (
                                                        <button onClick={() => handleMarkLiabilityPaid(liability.id)} className="text-xs text-indigo-600 hover:underline">Mark Paid</button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Beneficiaries Tab */}
                    {activeTab === 'beneficiaries' && (
                        <div>
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">Beneficiaries / Heirs</h3>
                                <button onClick={() => setShowAddBeneficiary(true)} className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors">
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                                    Add Beneficiary
                                </button>
                            </div>
                            {beneficiaries.length === 0 ? (
                                <div className="text-center py-12 text-gray-500">
                                    <p className="mb-2">No beneficiaries recorded yet</p>
                                    <button onClick={() => setShowAddBeneficiary(true)} className="text-indigo-600 hover:underline text-sm">Add the first beneficiary</button>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {beneficiaries.map(ben => (
                                        <div key={ben.id} className="p-4 bg-gray-50 rounded-lg">
                                            <div className="flex items-start justify-between">
                                                <div>
                                                    <p className="font-medium text-gray-900">{ben.full_name}</p>
                                                    <p className="text-sm text-gray-500">{ben.relationship_display}</p>
                                                    {ben.is_minor && <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full mt-1 inline-block">Minor</span>}
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-lg font-bold text-indigo-600">{ben.share_percentage}%</p>
                                                    <p className="text-xs text-gray-500">Share</p>
                                                </div>
                                            </div>
                                            {ben.specific_bequest && <p className="text-sm text-gray-600 mt-2 bg-white rounded p-2">{ben.specific_bequest}</p>}
                                            {ben.contact_email && <p className="text-xs text-gray-400 mt-1">{ben.contact_email}</p>}
                                        </div>
                                    ))}
                                </div>
                            )}
                            {beneficiaries.length > 0 && (
                                <div className="mt-4 p-3 bg-indigo-50 rounded-lg">
                                    <p className="text-sm text-indigo-700">
                                        Total share allocated: <span className="font-bold">{beneficiaries.reduce((sum, b) => sum + parseFloat(b.share_percentage || 0), 0).toFixed(2)}%</span>
                                        {beneficiaries.reduce((sum, b) => sum + parseFloat(b.share_percentage || 0), 0) !== 100 &&
                                            <span className="text-yellow-600 ml-2">(Warning: Does not total 100%)</span>
                                        }
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Timeline Tab */}
                    {activeTab === 'timeline' && (
                        <div>
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">Milestones & Deadlines</h3>
                                <button onClick={() => setShowAddMilestone(true)} className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors">
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                                    Add Milestone
                                </button>
                            </div>
                            {timeline.length === 0 ? (
                                <div className="text-center py-12 text-gray-500">
                                    <p className="mb-2">No timeline events yet</p>
                                    <button onClick={() => setShowAddMilestone(true)} className="text-indigo-600 hover:underline text-sm">Add the first milestone</button>
                                </div>
                            ) : (
                                <div className="relative">
                                    <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                                    <div className="space-y-4">
                                        {timeline.map(event => (
                                            <div key={event.id} className="relative flex items-start pl-10">
                                                <div className={`absolute left-2.5 w-3 h-3 rounded-full border-2 ${event.is_completed ? 'bg-green-500 border-green-500' : 'bg-white border-indigo-500'}`}></div>
                                                <div className="flex-1 bg-gray-50 rounded-lg p-4">
                                                    <div className="flex items-center justify-between">
                                                        <div>
                                                            <p className={`font-medium ${event.is_completed ? 'text-gray-500 line-through' : 'text-gray-900'}`}>{event.title}</p>
                                                            <p className="text-sm text-gray-500">{event.milestone_type_display}</p>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            {event.due_date && <span className="text-sm text-gray-500">{event.due_date}</span>}
                                                            {!event.is_completed && (
                                                                <button onClick={() => handleCompleteMilestone(event.id)} className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors">
                                                                    Complete
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {event.description && <p className="text-sm text-gray-600 mt-1">{event.description}</p>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Documents Tab */}
                    {activeTab === 'documents' && (
                        <div>
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">Estate Documents</h3>
                            </div>
                            {documents.length === 0 ? (
                                <div className="text-center py-12 text-gray-500">
                                    <p>No documents uploaded yet</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {documents.map(doc => (
                                        <div key={doc.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                            <div className="flex items-center">
                                                <div className="p-2 bg-indigo-100 rounded-lg">
                                                    <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                                </div>
                                                <div className="ml-3">
                                                    <p className="font-medium text-gray-900">{doc.title}</p>
                                                    <p className="text-sm text-gray-500">{doc.document_type_display}{doc.document_date ? ` • ${doc.document_date}` : ''}</p>
                                                </div>
                                            </div>
                                            <span className="text-xs text-gray-400">by {doc.uploaded_by_name}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Add Asset Modal */}
            {showAddAsset && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4">
                        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowAddAsset(false)}></div>
                        <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full p-8 z-10">
                            <h2 className="text-xl font-bold text-gray-900 mb-6">Add Asset</h2>
                            <form onSubmit={handleAddAsset} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Asset Type *</label>
                                    <select name="asset_type" required className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500">
                                        <option value="REAL_PROPERTY">Real Property</option>
                                        <option value="BANK_ACCOUNT">Bank Account</option>
                                        <option value="INVESTMENT">Investments</option>
                                        <option value="VEHICLE">Motor Vehicle</option>
                                        <option value="PERSONAL_PROPERTY">Personal Property</option>
                                        <option value="BUSINESS_INTEREST">Business Interest</option>
                                        <option value="LIFE_INSURANCE">Life Insurance</option>
                                        <option value="PENSION">Pension / Retirement</option>
                                        <option value="OTHER">Other</option>
                                    </select>
                                </div>
                                <div><label className="block text-sm font-medium text-gray-700 mb-1">Description *</label><input type="text" name="description" required className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" /></div>
                                <div><label className="block text-sm font-medium text-gray-700 mb-1">Estimated Value *</label><input type="number" name="estimated_value" step="0.01" min="0" required className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" /></div>
                                <div><label className="block text-sm font-medium text-gray-700 mb-1">Location</label><input type="text" name="location" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" /></div>
                                <div><label className="block text-sm font-medium text-gray-700 mb-1">Reference Number</label><input type="text" name="reference_number" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" placeholder="Title deed, account no., etc." /></div>
                                <div className="flex justify-end gap-3 pt-4"><button type="button" onClick={() => setShowAddAsset(false)} className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button><button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Add Asset</button></div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Liability Modal */}
            {showAddLiability && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4">
                        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowAddLiability(false)}></div>
                        <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full p-8 z-10">
                            <h2 className="text-xl font-bold text-gray-900 mb-6">Add Liability</h2>
                            <form onSubmit={handleAddLiability} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Liability Type *</label>
                                    <select name="liability_type" required className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500">
                                        <option value="MORTGAGE">Mortgage / Bond</option>
                                        <option value="LOAN">Personal Loan</option>
                                        <option value="CREDIT_CARD">Credit Card</option>
                                        <option value="TAX">Tax Liability</option>
                                        <option value="FUNERAL">Funeral Expenses</option>
                                        <option value="MEDICAL">Medical Expenses</option>
                                        <option value="ADMINISTRATION">Administration Costs</option>
                                        <option value="LEGAL_FEES">Legal Fees</option>
                                        <option value="OTHER">Other</option>
                                    </select>
                                </div>
                                <div><label className="block text-sm font-medium text-gray-700 mb-1">Creditor Name *</label><input type="text" name="creditor_name" required className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" /></div>
                                <div><label className="block text-sm font-medium text-gray-700 mb-1">Description *</label><input type="text" name="description" required className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" /></div>
                                <div><label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label><input type="number" name="amount" step="0.01" min="0.01" required className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" /></div>
                                <div className="flex justify-end gap-3 pt-4"><button type="button" onClick={() => setShowAddLiability(false)} className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button><button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Add Liability</button></div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Beneficiary Modal */}
            {showAddBeneficiary && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4">
                        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowAddBeneficiary(false)}></div>
                        <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full p-8 z-10">
                            <h2 className="text-xl font-bold text-gray-900 mb-6">Add Beneficiary</h2>
                            <form onSubmit={handleAddBeneficiary} className="space-y-4">
                                <div><label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label><input type="text" name="full_name" required className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" /></div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Relationship *</label>
                                    <select name="relationship" required className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500">
                                        <option value="SPOUSE">Spouse</option>
                                        <option value="CHILD">Child</option>
                                        <option value="PARENT">Parent</option>
                                        <option value="SIBLING">Sibling</option>
                                        <option value="GRANDCHILD">Grandchild</option>
                                        <option value="OTHER_RELATIVE">Other Relative</option>
                                        <option value="FRIEND">Friend</option>
                                        <option value="CHARITY">Charity</option>
                                        <option value="TRUST">Trust</option>
                                        <option value="OTHER">Other</option>
                                    </select>
                                </div>
                                <div><label className="block text-sm font-medium text-gray-700 mb-1">Share Percentage *</label><input type="number" name="share_percentage" step="0.01" min="0" max="100" required className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" /></div>
                                <div><label className="block text-sm font-medium text-gray-700 mb-1">Email</label><input type="email" name="contact_email" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" /></div>
                                <div><label className="block text-sm font-medium text-gray-700 mb-1">Phone</label><input type="text" name="contact_phone" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" /></div>
                                <div className="flex items-center gap-2"><input type="checkbox" name="is_minor" id="is_minor" className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" /><label htmlFor="is_minor" className="text-sm text-gray-700">Beneficiary is a minor</label></div>
                                <div><label className="block text-sm font-medium text-gray-700 mb-1">Specific Bequest</label><textarea name="specific_bequest" rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" placeholder="Specific items or amounts..." /></div>
                                <div className="flex justify-end gap-3 pt-4"><button type="button" onClick={() => setShowAddBeneficiary(false)} className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button><button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Add Beneficiary</button></div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Milestone Modal */}
            {showAddMilestone && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4">
                        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowAddMilestone(false)}></div>
                        <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full p-8 z-10">
                            <h2 className="text-xl font-bold text-gray-900 mb-6">Add Milestone</h2>
                            <form onSubmit={handleAddMilestone} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Milestone Type *</label>
                                    <select name="milestone_type" required className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500">
                                        <option value="FILING">Court Filing</option>
                                        <option value="DEADLINE">Deadline</option>
                                        <option value="HEARING">Court Hearing</option>
                                        <option value="NOTICE">Notice Published</option>
                                        <option value="DISTRIBUTION">Distribution Event</option>
                                        <option value="MEETING">Meeting</option>
                                        <option value="CORRESPONDENCE">Correspondence</option>
                                        <option value="OTHER">Other</option>
                                    </select>
                                </div>
                                <div><label className="block text-sm font-medium text-gray-700 mb-1">Title *</label><input type="text" name="title" required className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" /></div>
                                <div><label className="block text-sm font-medium text-gray-700 mb-1">Description</label><textarea name="description" rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" /></div>
                                <div><label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label><input type="date" name="due_date" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" /></div>
                                <div className="flex justify-end gap-3 pt-4"><button type="button" onClick={() => setShowAddMilestone(false)} className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button><button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Add Milestone</button></div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EstateDetail;
